import React, { useEffect, useState } from 'react';
import { validateFunctionalForm } from '../../utils';
import { v2_schedule_categories, v2_event_type } from '../../actions/v2/masterData';
import { v2_ships, v2_projects } from '../../actions/v2/projectAndVessel';
import constant from '../../constant';

const EventScheduleForm = ({ data, onSubmit }) => {
    const [state, setState] = useState({
        eventTypeId: { name: 'eventTypeId', value: data?.EVENT_TYPE_ID || '', options: [], isRequired: true, error: '' },
        categoryId: { name: 'categoryId', value: data?.CATEGORY_ID || '', options: [], isRequired: true, error: '' },
        projectId: { name: 'projectId', value: data?.PROJECT_ID || '', options: [], isRequired: true, error: '' },
        hull: { name: 'hull', value: data?.SHIP_ID || '', options: [], isRequired: true, error: '' },
        startDate: { name: 'startDate', value: data?.START_DATE || '', isRequired: true, error: '' },
        endDate: { name: 'endDate', value: data?.END_DATE || '', isRequired: true, error: '' },
        note: { name: 'note', value: data?.NOTE || '', isRequired: false, error: '' },
    });

    useEffect(() => {
        v2_schedule_categories().then(res => {
            if (res.status) {
                setState(prev => ({
                    ...prev,
                    categoryId: { ...prev.categoryId, options: res.result }
                }));
            }
        });

        v2_projects().then(res => {
            if (res.status) {
                setState(prev => ({
                    ...prev,
                    projectId: { ...prev.projectId, options: res.result }
                }));
            }
        });

        v2_ships().then(res => {
            if (res.status) {
                setState(prev => ({
                    ...prev,
                    hull: { ...prev.hull, options: res.result }
                }));
            }
        });
    }, []);

    useEffect(() => {
        if (state.categoryId.value) {
            v2_event_type(state.categoryId.value).then(res => {
                if (res.status) {
                    setState(prev => ({
                        ...prev,
                        eventTypeId: { ...prev.eventTypeId, options: res.result }
                    }));
                }
            });
        } else {
            setState(prev => ({
                ...prev,
                eventTypeId: { ...prev.eventTypeId, options: [], value: '' }
            }));
        }
    }, [state.categoryId.value]);

    useEffect(() => {
        if (state.projectId.value) {
            v2_ships(null, state.projectId.value).then(res => {
                if (res.status) {
                    setState(prev => ({
                        ...prev,
                        hull: { ...prev.hull, options: res.result }
                    }));
                }
            });
        } else {
            v2_ships().then(res => {
                if (res.status) {
                    setState(prev => ({
                        ...prev,
                        hull: { ...prev.hull, options: res.result, value: '' }
                    }));
                }
            });
        }
    }, [state.projectId.value]);

    const handleChange = (e) => {
        const { name, type, value, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;

        setState(prev => ({
            ...prev,
            [name]: {
                ...prev[name],
                value: val,
                error: prev[name].isRequired && !val ? 'This field is required' : ''
            }
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateFunctionalForm(state, setState)) {
            const model = {};
            Object.keys(state).forEach(key => {
                model[key] = state[key].value;
            });
            if (data?.ID) model.id = data.ID;
            onSubmit(model);
        }
    };

    const handleReset = () => {
        const resetState = { ...state };
        Object.keys(resetState).forEach(key => {
            resetState[key].value = data?.[key.toUpperCase()] || (key === 'isActive' ? true : '');
            resetState[key].error = '';
        });
        setState(resetState);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="row">
                {/* Project */}
                <div className="col-md-4 mb-3">
                    <label>Project {state.projectId.isRequired && <span className='text-danger'> *</span>}</label>
                    <select
                        name={state.projectId.name}
                        value={state.projectId.value}
                        onChange={handleChange}
                        className={`form-control ${state.projectId.error ? 'is-invalid' : ''}`}
                    >
                        <option value="">Select Project</option>
                        {state.projectId.options.map(opt => (
                            <option key={opt.ID} value={opt.ID}>{opt.PROJECT_NAME}-{opt.PROJECT_CODE}</option>
                        ))}
                    </select>
                    {state.projectId.error && <div className="invalid-feedback">{state.projectId.error}</div>}
                </div>

                {/* Hull */}
                <div className="col-md-4 mb-3">
                    <label>Hull {state.hull.isRequired && <span className='text-danger'> *</span>}</label>
                    <select
                        name={state.hull.name}
                        value={state.hull.value}
                        onChange={handleChange}
                        className={`form-control ${state.hull.error ? 'is-invalid' : ''}`}
                    >
                        <option value="">Select Hull</option>
                        {state.hull.options.map(opt => (
                            <option key={opt.ID} value={opt.ID}>{opt.HULL_NUMBER}-{opt.SHIP_NAME}</option>
                        ))}
                    </select>
                    {state.hull.error && <div className="invalid-feedback">{state.hull.error}</div>}
                </div>

                {/* Category */}
                <div className="col-md-4 mb-3">
                    <label>Category {state.categoryId.isRequired && <span className='text-danger'> *</span>}</label>
                    <select
                        name={state.categoryId.name}
                        value={state.categoryId.value}
                        onChange={handleChange}
                        className={`form-control ${state.categoryId.error ? 'is-invalid' : ''}`}
                    >
                        <option value="">Select Category</option>
                        {state.categoryId.options.map(opt => (
                            <option key={opt.ID} value={opt.ID}>{opt.CATEGORY_NAME}</option>
                        ))}
                    </select>
                    {state.categoryId.error && <div className="invalid-feedback">{state.categoryId.error}</div>}
                </div>

                {/* Event Type */}
                <div className="col-md-4 mb-3">
                    <label>Event Type {state.eventTypeId.isRequired && <span className='text-danger'> *</span>}</label>
                    <select
                        name={state.eventTypeId.name}
                        value={state.eventTypeId.value}
                        onChange={handleChange}
                        className={`form-control ${state.eventTypeId.error ? 'is-invalid' : ''}`}
                    >
                        <option value="">Select Event Type</option>
                        {state.eventTypeId.options.map(opt => (
                            <option key={opt.ID} value={opt.ID}>{opt.EVENT_TYPE_NAME}</option>
                        ))}
                    </select>
                    {state.eventTypeId.error && <div className="invalid-feedback">{state.eventTypeId.error}</div>}
                </div>

                {/* Start Date */}
                <div className="col-md-4 mb-3">
                    <label>Start Date {state.startDate.isRequired && <span className='text-danger'> *</span>}</label>
                    <input
                        type="date"
                        name={state.startDate.name}
                        value={state.startDate.value}
                        onChange={handleChange}
                        className={`form-control ${state.startDate.error ? 'is-invalid' : ''}`}
                    />
                    {state.startDate.error && <div className="invalid-feedback">{state.startDate.error}</div>}
                </div>

                {/* End Date */}
                <div className="col-md-4 mb-3">
                    <label>End Date {state.endDate.isRequired && <span className='text-danger'> *</span>}</label>
                    <input
                        type="date"
                        name={state.endDate.name}
                        value={state.endDate.value}
                        onChange={handleChange}
                        className={`form-control ${state.endDate.error ? 'is-invalid' : ''}`}
                    />
                    {state.endDate.error && <div className="invalid-feedback">{state.endDate.error}</div>}
                </div>

                {/* Note */}
                <div className="col-md-12 mb-3">
                    <label>Note {state.note.isRequired && <span className='text-danger'> *</span>}</label>
                    <textarea
                        name={state.note.name}
                        value={state.note.value}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter additional notes"
                        rows="3"
                    />
                </div>
            </div>

            <div className="mt-3">
                <button type="submit" className="btn btn-success me-2">Save</button>
                <a type="button" className="btn btn-danger text-white" href={constant.Authedcomponent.schedulePreview.url} >Cancel</a>
            </div>
        </form>
    );
};

export default EventScheduleForm
